import React, { useState, useEffect } from 'react';
import { X, ChevronRight, ChevronLeft, BarChart3, Euro, Clock, Wallet, Users, Package } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const VENDOR_ONBOARDING_KEY = 'iptv4kfr_vendor_onboarding_done';

const steps = [
  {
    icon: <BarChart3 className="h-8 w-8 text-[#FF6B35]" />,
    title: "Suivi des commissions",
    desc: "Votre tableau de bord affiche en temps r\u00e9el vos statistiques de ventes, votre palier actuel et vos commissions. Plus vous vendez, plus votre commission par vente augmente.",
    position: 'center'
  },
  {
    icon: <Euro className="h-8 w-8 text-[#00FF88]" />,
    title: "Solde disponible",
    desc: "Le solde disponible repr\u00e9sente les commissions valid\u00e9es et pr\u00eates \u00e0 \u00eatre retir\u00e9es. Vous pouvez demander un retrait d\u00e8s que votre solde atteint 50\u20ac.",
    position: 'center'
  },
  {
    icon: <Clock className="h-8 w-8 text-[#FFB800]" />,
    title: "Solde en attente",
    desc: "Les commissions sur les nouvelles ventes passent d'abord en \"attente\" le temps de la validation par l'administrateur. Une fois valid\u00e9es, elles passent en \"disponible\".",
    position: 'center'
  },
  {
    icon: <Package className="h-8 w-8 text-[#00FFFF]" />,
    title: "Historique des ventes",
    desc: "Consultez l'historique d\u00e9taill\u00e9 de toutes vos ventes : client, date, statut, commission. Utilisez les filtres pour analyser vos performances.",
    position: 'center'
  },
  {
    icon: <Wallet className="h-8 w-8 text-[#F7931E]" />,
    title: "Retraits",
    desc: "Demandez un retrait depuis votre espace vendeur. Le paiement est effectu\u00e9 via PayPal apr\u00e8s validation admin. Seuil minimum : 50\u20ac.",
    position: 'center'
  },
  {
    icon: <Users className="h-8 w-8 text-[#00FF88]" />,
    title: "Parrainage vendeur",
    desc: "Quand un de vos clients parraine un ami, vous touchez aussi une commission de 5\u20ac. C'est un revenu passif qui s'accumule automatiquement !",
    position: 'center'
  }
];

const VendorOnboarding = () => {
  const [visible, setVisible] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);

  useEffect(() => {
    const done = localStorage.getItem(VENDOR_ONBOARDING_KEY);
    if (!done) {
      const timer = setTimeout(() => setVisible(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleClose();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) setCurrentStep(currentStep - 1);
  };

  const handleClose = () => {
    localStorage.setItem(VENDOR_ONBOARDING_KEY, 'true');
    setVisible(false);
  };

  if (!visible) return null;

  const step = steps[currentStep];

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[100]" data-testid="vendor-onboarding">
        <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />

        <motion.div
          key={currentStep}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-md px-4"
        >
          <div className="bg-[#1A1A1D] rounded-xl border border-[#00FFFF]/30 shadow-2xl shadow-[#00FFFF]/10 overflow-hidden">
            <div className="bg-gradient-to-r from-[#00FFFF] to-[#00B8D4] px-6 py-4 flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <span className="text-black text-sm font-bold">Guide vendeur</span>
                <span className="bg-black/20 text-black text-xs px-2 py-0.5 rounded-full font-medium">
                  {currentStep + 1}/{steps.length}
                </span>
              </div>
              <button onClick={handleClose} className="text-black/60 hover:text-black" data-testid="close-vendor-onboarding">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6">
              <div className="flex items-center space-x-4 mb-4">
                <div className="p-3 rounded-xl bg-[#222226] border border-[#3A3A3D]">
                  {step.icon}
                </div>
                <h3 className="text-xl font-bold text-white">{step.title}</h3>
              </div>
              <p className="text-[#B8B8BA] text-sm leading-relaxed mb-6">{step.desc}</p>

              <div className="flex items-center justify-center space-x-2 mb-6">
                {steps.map((_, i) => (
                  <div
                    key={i}
                    className={`h-1.5 rounded-full transition-all ${
                      i === currentStep ? 'w-6 bg-[#00FFFF]' : i < currentStep ? 'w-1.5 bg-[#00FFFF]/50' : 'w-1.5 bg-[#3A3A3D]'
                    }`}
                  />
                ))}
              </div>

              <div className="flex items-center justify-between">
                <button
                  onClick={handleClose}
                  className="text-[#6B6B6E] hover:text-white text-sm transition-colors"
                  data-testid="skip-vendor-onboarding"
                >
                  Passer
                </button>
                <div className="flex items-center space-x-2">
                  {currentStep > 0 && (
                    <button
                      onClick={handlePrev}
                      className="p-2 rounded-lg bg-[#222226] border border-[#3A3A3D] text-white hover:border-[#00FFFF] transition-colors"
                      data-testid="prev-vendor-step"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </button>
                  )}
                  <button
                    onClick={handleNext}
                    className="px-5 py-2 rounded-lg bg-gradient-to-r from-[#00FFFF] to-[#00B8D4] text-black font-bold text-sm hover:shadow-lg hover:shadow-[#00FFFF]/30 transition-all flex items-center space-x-1"
                    data-testid="next-vendor-step"
                  >
                    <span>{currentStep === steps.length - 1 ? "C'est parti !" : 'Suivant'}</span>
                    {currentStep < steps.length - 1 && <ChevronRight className="h-4 w-4" />}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export { VENDOR_ONBOARDING_KEY };
export default VendorOnboarding;
